import { useQuery } from '@tanstack/react-query'
import { Check, Share2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { perspectiveShareUrl } from '@/lib/perspective-share'
import { useTRPC } from '@/lib/trpc'

interface PerspectiveShareButtonProps {
  className?: string
  gameId: string
  labelClassName?: string
  playerId: string
}

export function PerspectiveShareButton({
  className,
  gameId,
  labelClassName,
  playerId,
}: PerspectiveShareButtonProps) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const [copied, setCopied] = useState(false)
  const tokenQuery = useQuery({
    ...trpc.playRouter.perspectiveShareToken.queryOptions({
      gameId,
      playerId,
    }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const shareUrl = tokenQuery.data?.token
    ? perspectiveShareUrl(gameId, tokenQuery.data.token)
    : ''

  const handleShare = useCallback(async () => {
    if (!shareUrl)
      return

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: shareUrl })
        return
      }
      catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError')
          return
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success(t('play.spectatorLinkCopied'))
      setTimeout(() => setCopied(false), 2000)
    }
    catch {
      toast.error(t('play.spectatorLinkShareFailed'))
    }
  }, [shareUrl, t])

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      data-testid="spectator-link-button"
      aria-label={t('play.spectatorLink')}
      title={t('play.spectatorLink')}
      disabled={!shareUrl}
      onClick={handleShare}
    >
      {copied ? (
        <Check className="size-3.5 text-green-500" />
      ) : (
        <Share2 className="size-3.5" />
      )}
      {labelClassName !== undefined && (
        <span className={labelClassName}>{t('play.spectatorLink')}</span>
      )}
    </Button>
  )
}
